import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { TopBar } from '@/components/layout/TopBar';
import { PageContainer } from '@/components/layout/PageContainer';
import { OTPInput } from '@/components/ui/OTPInput';
import { useRequestEmailCode, useVerifyEmailCode } from '@/api/queries/auth';
import { useAuthStore } from '@/store/auth';
import { paths } from '@/routes/paths';
import { useUIStore } from '@/store/ui';
import { isAPIError } from '@/api/error';

const OTP_LEN = 6;

export function LoginOtpPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const email = (location.state as { email?: string } | null)?.email ?? '';
  const [code, setCode] = useState('');
  const [resendIn, setResendIn] = useState(60);

  const verify = useVerifyEmailCode();
  const resend = useRequestEmailCode();
  const setHasToken = useAuthStore((s) => s.setHasToken);
  const pushToast = useUIStore((s) => s.pushToast);

  useEffect(() => {
    if (!email) {
      navigate(paths.loginEmail, { replace: true });
    }
  }, [email, navigate]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => setResendIn((x) => x - 1), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  useEffect(() => {
    if (code.length === OTP_LEN && email) {
      void (async () => {
        try {
          const r = await verify.mutateAsync({ email, code });
          setHasToken(true);
          navigate(r.user.onboarding_completed ? paths.home : paths.onboardingProfile, {
            replace: true,
          });
        } catch (err) {
          const key = isAPIError(err) ? err.i18nKey : 'errors.generic';
          pushToast({
            body: t(key, { defaultValue: t('auth.otp.invalid') }),
            variant: 'error',
          });
          setCode('');
        }
      })();
    }
  }, [code, email, verify, setHasToken, navigate, pushToast, t]);

  const handleResend = async () => {
    if (!email || resendIn > 0) return;
    try {
      await resend.mutateAsync(email);
      setResendIn(60);
      pushToast({ body: t('auth.email.send_code'), variant: 'info' });
    } catch {
      /* noop */
    }
  };

  return (
    <PageContainer>
      <TopBar back title={t('auth.otp.title')} />
      <p style={{ textAlign: 'center', color: 'var(--color-text-on-dark-muted)' }}>
        {t('auth.otp.subtitle', { email })}
      </p>
      <OTPInput length={OTP_LEN} value={code} onChange={setCode} ariaLabel={t('auth.otp.title')} />

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
        {resendIn > 0 ? (
          <span style={{ color: 'var(--color-text-on-dark-muted)' }}>
            {t('auth.email.resend_in', { seconds: resendIn })}
          </span>
        ) : (
          <Button variant="plain" onClick={handleResend} loading={resend.isPending}>
            {t('auth.email.resend')}
          </Button>
        )}
      </div>
    </PageContainer>
  );
}
